import { useEffect, useState } from 'react';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import StorageIcon from '@material-ui/icons/Storage';
import { useApi, discoveryApiRef, identityApiRef } from '@backstage/core-plugin-api';

/**
 * Kubernetes 集群选项
 */
interface KubernetesClusterOption {
  name: string;
  title: string;
  url: string;
}

/**
 * KubernetesClusterPicker
 *
 * 自定义字段：从 app-config 中获取已配置的 Kubernetes 集群列表
 *
 * 使用方式：
 * ```yaml
 * clusterName:
 *   title: Kubernetes 集群
 *   type: string
 *   ui:field: KubernetesClusterPicker
 * ```
 *
 * 读取配置：
 * ```yaml
 * kubernetes:
 *   clusterLocatorMethods:
 *     - type: 'config'
 *       clusters:
 *         - name: my-cluster
 *           title: My Cluster
 *           url: https://...
 * ```
 */
interface KubernetesClusterPickerProps {
  onChange: (value: string | undefined) => void;
  rawErrors?: string[];
  required?: boolean;
  formData?: string;
  idSchema?: { $id: string };
  schema?: { title?: string; description?: string };
}

export const KubernetesClusterPicker = ({
  onChange,
  rawErrors,
  required,
  formData,
  idSchema,
  schema,
}: KubernetesClusterPickerProps) => {
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);

  const [clusters, setClusters] = useState<KubernetesClusterOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  useEffect(() => {
    const loadClusters = async () => {
      try {
        setLoading(true);
        setError(undefined);

        // 获取后端基础 URL 和用户认证 token
        // 模块挂载在 scaffolder 插件下，路径为 /api/scaffolder/k8s-cluster-resolver/clusters
        const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
        const { token } = await identityApi.getCredentials();

        // 通过后端 API 获取集群列表（携带认证 token）
        const response = await fetch(`${baseUrl}/k8s-cluster-resolver/clusters`, {
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error('未授权：请重新登录');
          }
          throw new Error(`Failed to fetch clusters: ${response.status}`);
        }

        const data = await response.json();
        const clusterList: KubernetesClusterOption[] = data.clusters || [];

        if (clusterList.length === 0) {
          setError('未配置 Kubernetes 集群');
        }

        // 按名称排序
        clusterList.sort((a, b) => a.name.localeCompare(b.name));

        setClusters(clusterList);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to load Kubernetes clusters',
        );
      } finally {
        setLoading(false);
      }
    };

    loadClusters();
  }, [discoveryApi, identityApi]);

  const fieldId = idSchema?.$id || 'kubernetes-cluster-picker';

  return (
    <FormControl
      margin="normal"
      required={required}
      error={(rawErrors?.length ?? 0) > 0 && !formData}
      fullWidth
    >
      <InputLabel htmlFor={fieldId}>
        {schema?.title || 'Kubernetes Cluster'}
      </InputLabel>
      <Select
        id={fieldId}
        value={formData || ''}
        onChange={e => onChange(e.target.value as string)}
        disabled={loading}
      >
        {loading && (
          <MenuItem value="" disabled>
            加载中...
          </MenuItem>
        )}
        {error && (
          <MenuItem value="" disabled>
            {error}
          </MenuItem>
        )}
        {!loading && !error && clusters.length === 0 && (
          <MenuItem value="" disabled>
            无可用集群
          </MenuItem>
        )}
        {clusters.map(cluster => (
          <MenuItem key={cluster.name} value={cluster.name}>
            <Box display="flex" alignItems="center" width="100%">
              <StorageIcon
                fontSize="small"
                style={{ marginRight: 8, color: '#326ce5' }}
              />
              <Box>
                <Typography variant="body2" style={{ fontWeight: 500 }}>
                  {cluster.title}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {cluster.name} - {cluster.url}
                </Typography>
              </Box>
            </Box>
          </MenuItem>
        ))}
      </Select>
      {(rawErrors?.length ?? 0) > 0 && !formData && (
        <FormHelperText error>此字段为必填项</FormHelperText>
      )}
      {formData &&
        (() => {
          const selected = clusters.find(c => c.name === formData);
          return selected ? (
            <FormHelperText>
              已选择: {selected.title} ({selected.url})
            </FormHelperText>
          ) : null;
        })()}
    </FormControl>
  );
};

